import {_, React, observer, observable, computed, classNames, useAsObservableSource, IDisposer} from '@api'
import {CatalogDialogs, ContextMenuItem, ErrorBoundary, FlexLayoutContextMenu, IContextMenuItems, RightSidebarBody, StoresInstanceContext, useVipLocalStore, VipLocalStore} from '@modules'
import {FlexLayoutComponentProps, globalStores, icons, StoresInstance} from '@stores'
import {IFlexLayoutCatalogRootConfig, IFlexLayoutInlineCatalogItemDetailsConfig} from '@stores/catalog/flexLayout'
import {TabNode} from '@flexlayout'
import {action, autorun, reaction, runInAction, toJS, trace} from 'mobx'
import {Fragment, useEffect} from 'react'
import * as css from './FlexLayoutInlineCatalogItemDetailsPane.css'
import {Icon} from '@blueprintjs/core'

/// region Store

interface MyProps extends FlexLayoutComponentProps {
  className?: string
}

export class FlexLayoutInlineCatalogItemDetailsPaneStore extends VipLocalStore implements IContextMenuItems {
  constructor(public props: MyProps, stores: StoresInstance) {
    super(stores)
  }

  get contextMenuItems() {
    return <FlexLayoutInlineCatalogItemDetailsPaneStoreContext.Provider value={this}>
      <FlexLayoutContextMenu />
    </FlexLayoutInlineCatalogItemDetailsPaneStoreContext.Provider>
  }

  @observable.ref overriddenStores: Partial<StoresInstance>

  get tabConfig() { return this.props.flexNode.config as IFlexLayoutInlineCatalogItemDetailsConfig}

  @computed get tabIcon(): React.ReactNode {
    return null

    const {overriddenStores: {sidebar: {right: {selectedNavStore}}}} = this
    return (
      <div className={css.tabIcon}>
        <Icon icon={icons.switchDetailViewLocation}/>
      </div>)
  }

  get tab(): TabNode<IFlexLayoutInlineCatalogItemDetailsConfig> { return this.props.flexNode}

  @computed get parentTab() {
    const {flexLayout, tabConfig: {settings: {parentTab, workspace}}} = this
    if (!parentTab) return null
    const tab = flexLayout.model?.nodeById<TabNode<IFlexLayoutCatalogRootConfig>>(parentTab)
    if (tab?.config?.settings?.workspace !== workspace) return null

    return tab
  }

  registerEffects = () => {
    // console.log(`Mount FlexLayoutInlineCatalogItemDetailsPaneStore ${this.node.id}`)
    this._toDispose.push(
      autorun(() => {
        if (this.overriddenStores?.purpose === 'inline-details') {
          this.tab.extraData.store = this
        }
      }),
      autorun(() => {
        if (this.overriddenStores?.disposed) {
          this.overriddenStores = null
          this.tab.extraData.store = null
        }
      }),
      autorun(() => {
        // console.log(`Creating new sidebar details store`)
        const {props: {flexNode}, workspaces: {wrapperById}, tabConfig: {settings: {workspace}}} = this,
          ws = wrapperById[workspace]

        if (!ws) {
          this.tab.removeFromParent()
        }
        else {
          // trace()
          runInAction(() => {
            this.overriddenStores && this.overriddenStores.dispose()
            this.overriddenStores = new StoresInstance(
              `Individual Details Tab - ${flexNode.id} - ${ws.title}`,
              wrapperById[workspace],
              flexNode.id,
              'inline-details',
              {tab: flexNode})
          })
        }
      }),
      autorun(() => {
        const {tabConfig: {settings: {expanded, detailsPath, selectedPathIndex}}, workspaces: {byId}, overriddenStores} = this
        // console.log(toJS(settings))
        if (detailsPath && overriddenStores) {
          const {sidebar: {right}} = overriddenStores

          runInAction(() => {
            // Track selection as sidebar.right.path

            if (!_.isEqual(detailsPath, right.path)) {
              _.isNumber(selectedPathIndex) && (right.selectedPathIndex = selectedPathIndex)
              if (detailsPath) {
                right.path = detailsPath.slice()
                right.open = true
                right.headerExpanded = detailsPath.length <= 1 ? true : (right.headerExpanded || expanded)
                right.flexLocation = 'inlineCatalogItemDetails'
                right.tabNode = this.tab

                // console.log(toJS(_.pick(right, ['path', 'open']), {recurseEverything: true}))
              }
            }
          })

          if (right.details?.isOffline) {
            if (!right.details.doesOfflineItemExist && !right.details.isSyncing) {
              this.tab.removeFromParent(true)
            }
          }
        } else {
          // debugger
        }
      }, {name: `Set inline right sidebar path to match what flex layout tab's settings`}),
      autorun(() => {
        if (this.overriddenStores) {
          const {overriddenStores: {sidebar: {right: {selectedPathIndex, path, headerExpanded}}}} = this

          runInAction(() => {
            const {tabConfig} = this
            tabConfig.settings.selectedPathIndex = selectedPathIndex
            tabConfig.settings.detailsPath = path.slice()
            tabConfig.settings.expanded = headerExpanded
          })
        }
      }, {name: `Save tab configuration as user changes detail store path`}),
      this.tab.autorun_updateFlexNodeCustomSettings()
    )

    return () => {
      // console.log(`Unmount FlexLayoutInlineCatalogItemDetailsPaneStore ${this.node.id}`)
      this.overriddenStores?.sidebar?.dispose()
      this._toDispose.forEach(d => d && d())
    }
  }

  _toDispose: Array<IDisposer> = []

  @action onContextMenu = (e) => {
    this.flexLayout.onContextMenu_forTabNode(this.tab)(e)
  }
}

export const FlexLayoutInlineCatalogItemDetailsPaneStoreContext = React.createContext<FlexLayoutInlineCatalogItemDetailsPaneStore>(null)
export const useFlexLayoutInlineCatalogItemDetailsPaneStore = () => React.useContext(FlexLayoutInlineCatalogItemDetailsPaneStoreContext)

/// endregion

/// region Components

export const FlexLayoutInlineCatalogItemDetailsPane = observer<MyProps>(function FlexLayoutInlineCatalogItemDetailsPane({className, ...props}) {
  const obsProps = useAsObservableSource(_.pick(props, ['flexNode'])),
    store = useVipLocalStore((stores) => new FlexLayoutInlineCatalogItemDetailsPaneStore(obsProps, stores)),
    {overriddenStores, onContextMenu, registerEffects, tab, tab: {config: {settings: {detailsPath}}}, flexLayout: {isMaximized}} = store

  // useEffect(() => {
  //   console.log(`Mount FlexLayoutInlineCatalogItemDetailsPane ${id}`)
  //   return () => console.log(`Unmount FlexLayoutInlineCatalogItemDetailsPane ${id}`)
  // }, [])

  useEffect(() => registerEffects(), [])

  // If you do this the component will load/unload
  // if (isMaximized) return null  // This control is only for tool windows

  useEffect(() => {
    if (_.isEmpty(detailsPath)) {
      tab.removeFromParent(true)
    }
  }, [detailsPath])

  // console.log({workspace, deployment, id})
  return (
    <FlexLayoutInlineCatalogItemDetailsPaneStoreContext.Provider value={store}>
      <StoresInstanceContext.Provider value={overriddenStores as any}>
        <div className={classNames(css.root, className)}
             data-hidden={isMaximized}
             data-header-expanded={overriddenStores && overriddenStores.sidebar.right.headerExpanded}
             data-sidebar-type='modal'
             onContextMenu={onContextMenu}
             style={overriddenStores?.catalog?.rootStyle}>
          <ErrorBoundary>
            {_.isEmpty(detailsPath) ? <div className={css.invalidPath}>
              Invalid Configuration. Please close this tab and try again.
            </div> : <Fragment>
               <RightSidebarBody
                 {...props}
                 //topExtra={<DetailsModalNavToolbar className={css.navToolbar}/>}
               />
             </Fragment>}
            <CatalogDialogs/>
          </ErrorBoundary>
        </div>
      </StoresInstanceContext.Provider>
    </FlexLayoutInlineCatalogItemDetailsPaneStoreContext.Provider>
  )
})

/// endregion





// WEBPACK FOOTER //
// ./src/stores/catalog/flexLayout/flexComponents/toolbox-items/FlexLayoutInlineCatalogItemDetailsPane.tsx